import { Fragment, useMemo } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { RootReducer } from 'redux/reducers';
import { selectActiveDsTab } from 'redux/mode/reducers';
import { toggleDsPick } from 'redux/mode/actions';
import useScrollMemory from 'hooks/useScrollMemory';
import { DesignSystemToken } from 'designSystem/types';
import { getColorString } from 'designSystem/content/util';
import { groupColorsForGrid, ColorFamily } from './util';
import * as Base from '../styles';
import * as S from './styles';

const ColorsView = () => {
	const dispatch = useDispatch();
	const { designSystemContent, selectedDsPicks, activeTab } = useSelector(
		(r: RootReducer) => ({
			designSystemContent: r.mode.designSystemContent,
			selectedDsPicks: r.mode.selectedDsPicks,
			activeTab: selectActiveDsTab(r.mode),
		}),
		shallowEqual
	);

	const scrollRef = useScrollMemory(activeTab?.id ?? null);

	const modes = designSystemContent?.config?.display?.modes ?? [];
	const defaultModeId = modes.find((m) => m.default)?.id ?? modes[0]?.id;
	const nonDefaultModeIds = modes.map((m) => m.id).filter((id) => id !== defaultModeId);

	const themeColorTokens = useMemo(
		() => designSystemContent?.theme?.byCategory?.color ?? [],
		[designSystemContent?.theme?.byCategory?.color]
	);

	const stylesColorTokens = useMemo(
		() => designSystemContent?.styles?.tokens?.byCategory?.color ?? [],
		[designSystemContent?.styles?.tokens?.byCategory?.color]
	);

	const themeBaseTokens = useMemo(
		() => themeColorTokens.filter((t) => !t.mode),
		[themeColorTokens]
	);

	const stylesBaseTokens = useMemo(
		() => stylesColorTokens.filter((t) => !t.mode),
		[stylesColorTokens]
	);

	const stylesTokensByMode = useMemo(() => {
		const result: Record<string, DesignSystemToken[]> = {};
		for (const id of nonDefaultModeIds) {
			result[id] = stylesColorTokens.filter((t) => t.mode === id);
		}
		return result;
	}, [stylesColorTokens, nonDefaultModeIds]);

	const themeGridData = useMemo(() => groupColorsForGrid(themeBaseTokens), [themeBaseTokens]);

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------

	const isTokenSelected = (token: DesignSystemToken) =>
		selectedDsPicks.some((p) => p.kind === 'token' && p.cssVariable === token.cssVariable);

	const onSwatchClick = (token: DesignSystemToken) => {
		dispatch(toggleDsPick({ kind: 'token', cssVariable: token.cssVariable }));
	};

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	const renderColorRampGrid = (families: ColorFamily[], allShades: string[]) => {
		if (families.length === 0) return null;
		return (
			<S.GridWrapper data-testid="ds-color-grid">
				<S.ColorGrid
					style={{
						gridTemplateColumns: `auto repeat(${allShades.length}, minmax(48px, 80px))`,
					}}
				>
					<div />
					{allShades.map((shade) => (
						<S.HeaderCell key={shade}>{shade}</S.HeaderCell>
					))}

					{families.map((family) => {
						const shadeMap = new Map(family.shades.map((s) => [s.shade, s.token]));
						return (
							<Fragment key={family.family}>
								<S.FamilyLabel>{family.family}</S.FamilyLabel>
								{allShades.map((shade) => {
									const token = shadeMap.get(shade);
									if (!token) {
										return <S.EmptyCell key={`${family.family}-${shade}`} />;
									}
									const color = getColorString(token);
									const selected = isTokenSelected(token);
									return (
										<S.SwatchCell
											key={token.id}
											type="button"
											data-testid="ds-color-swatch"
											$color={color}
											$selected={selected}
											aria-pressed={selected}
											onClick={() => onSwatchClick(token)}
										>
											<S.SwatchTooltip>
												{token.cssVariable}
												<br />
												{color}
											</S.SwatchTooltip>
										</S.SwatchCell>
									);
								})}
							</Fragment>
						);
					})}
				</S.ColorGrid>
			</S.GridWrapper>
		);
	};

	const renderUngroupedTiles = (tokens: DesignSystemToken[]) => {
		if (tokens.length === 0) return null;
		return (
			<S.UngroupedGrid>
				{tokens.map((token) => {
					const color = getColorString(token);
					const selected = isTokenSelected(token);
					return (
						<S.UngroupedSwatch
							key={token.id}
							type="button"
							$selected={selected}
							aria-pressed={selected}
							onClick={() => onSwatchClick(token)}
						>
							<S.UngroupedSwatchTooltip>{color}</S.UngroupedSwatchTooltip>
							<S.UngroupedSwatchColor $color={color} />
							<S.UngroupedSwatchLabel>
								<S.UngroupedSwatchName>{token.name}</S.UngroupedSwatchName>
								<S.UngroupedSwatchVariable>{token.cssVariable}</S.UngroupedSwatchVariable>
							</S.UngroupedSwatchLabel>
						</S.UngroupedSwatch>
					);
				})}
			</S.UngroupedGrid>
		);
	};

	const hasTheme = themeBaseTokens.length > 0;
	const hasSemantic = stylesBaseTokens.length > 0;

	return (
		<S.ScrollContainer ref={scrollRef}>
			<Base.SectionHeader>
				<Base.SectionTitle>Colors</Base.SectionTitle>
			</Base.SectionHeader>
			<Base.SectionContent>
				{!hasTheme && !hasSemantic && <p>No color tokens found.</p>}

				{hasTheme && (
					<S.Base>
						<Base.SectionDivider>Color Palette</Base.SectionDivider>
						{renderColorRampGrid(themeGridData.families, themeGridData.allShades)}
						{renderUngroupedTiles(themeGridData.ungrouped)}
					</S.Base>
				)}

				{hasSemantic && (
					<S.Base>
						<Base.SectionDivider>Semantic Colors</Base.SectionDivider>
						{renderUngroupedTiles(stylesBaseTokens)}
						{nonDefaultModeIds.map((modeId) => {
							const tokens = stylesTokensByMode[modeId] ?? [];
							if (tokens.length === 0) return null;
							const modeLabel = modes.find((m) => m.id === modeId)?.label ?? modeId;
							return (
								<Fragment key={modeId}>
									<S.ModeBandHeader>{modeLabel} Overrides</S.ModeBandHeader>
									{renderUngroupedTiles(tokens)}
								</Fragment>
							);
						})}
					</S.Base>
				)}
			</Base.SectionContent>
		</S.ScrollContainer>
	);
};

export default ColorsView;
